import type { SurveyResource } from '@dmp/qqms/data-access';
import { isDynamicCoreQuestion, isQuestion } from '@dmp/qqms/survey-utils';
import type { Card } from '@dmp/qqms/types';
import { omit } from 'remeda';
import { defaultUiState } from '../../state-defaults';
import type { AnswerState, CardState, StoreState } from '../../state-types';

/** Set survey from the data received from the resource and transform it into the state. This is used when the survey is loaded from the API. */
export const setSurveyFromResource =
    (surveyResource: SurveyResource) =>
    (state: StoreState): StoreState => {
        if (!surveyResource || !surveyResource['id']) {
            throw new Error('Incorrect SurveyResource from API');
        }

        const {
            config,
            name,
            qqid,
            liveProofUrl,
            placement,
            publicationStatus,
        } = surveyResource.attributes;
        const { cards, traxexLabel, aboutCopy, sponsorInfo, theme } = config;

        const survey = {
            id: surveyResource.id,
            qqid,
            name,
            traxexLabel,
            aboutCopy,
            sponsorInfo,
            liveProofUrl,
            cardIds: cards.map((c) => c.id),
            theme: {
                ...state.survey.theme,
                ...theme,
            },
            placement: placement ?? state.survey.placement,
            publicationStatus,
        };

        return {
            ...state,
            survey,
            cards: transformToCardsState(cards),
            answers: transformToAnswersState(cards),
            // set the survey pristine
            ui: {
                ...defaultUiState,
                isSurveyDirty: false,
            },
        };
    };

/**
 * Collect & transform cards resource to array of cards
 */
const transformToCardsState = (cards: Card[]): Record<string, CardState> =>
    cards.reduce((acc, card) => {
        if (isDynamicCoreQuestion(card)) {
            return {
                ...acc,
                [card.id]: {
                    ...card,
                    questionIds: card.questions.map((c) => c.id),
                },
            };
        }

        if (isQuestion(card)) {
            const questionCard = omit(card, ['answers']);
            return {
                ...acc,
                [card.id]: {
                    ...questionCard,
                    answerIds: card.answers.map((c) => c.id),
                },
            };
        }

        return { ...acc, [card.id]: card };
    }, {});

/**
 * Collect & transform answers resource to array of answers
 */
const transformToAnswersState = (cards: Card[]): Record<string, AnswerState> =>
    cards
        .map((card) => ('answers' in card ? card.answers : []))
        .flat()
        .reduce((acc, answer) => ({ ...acc, [answer.id]: answer }), {});
